import {
  base_colorContentBorder,
  text_colorTextSecondary,
} from 'ext-ui/style/colors'
import { spacing } from 'ext-ui/style/spacing'
import {
  font_family_weight_medium,
  font_family_weight_regular,
  font_family_weight_semibold,
  font_md,
  font_size_sm,
} from 'ext-ui/style/typography'
import { css } from 'styled-components'

/**
 * `ContentCard` ships spacing(9)/spacing(10) — sized for prose-ish panels. A
 * dense table reads as floating in it, so the vertical padding is trimmed.
 *
 * Kept SYMMETRIC: the bottom gap matches the top one above the heading. This
 * panel once dropped its bottom padding entirely, on the theory that the grid's
 * last row border was the panel's real edge — on screen that read as the rows
 * being cropped by the card.
 */
export const panel = css`
  display: flex;
  flex-direction: column;
  padding: ${spacing(6)} ${spacing(8)};
`

export const header = css`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${spacing(4)};
  margin-bottom: ${spacing(3)};
`

/**
 * Mirrors `cardDetailTabSectionTitle` (the Statements/Activity tab kicker) from
 * the same design spec. Duplicated rather than imported because that style lives
 * in `src/page-components/Accounts/` and a feature must not depend on a page.
 *
 * Uppercased via CSS, never in the translation source, so the accessible name
 * stays the plain phrase.
 */
export const title = css`
  font-size: ${font_size_sm};
  font-family: ${font_family_weight_semibold};
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${text_colorTextSecondary};
  margin: 0;
`

/** Keeps the grid's own top border from doubling the card's inner spacing. */
export const grid = css`
  border-top: 1px solid ${base_colorContentBorder};
`

/**
 * A column header, rendered by our own `renderHeader` rather than styled through
 * `.MuiDataGrid-columnHeaderTitle`.
 *
 * Identical to `CardBudgetsTable.style.columnHeader` — the two tables sit one
 * above the other on the Overview tab and their headers have to read as one
 * surface. Duplicated rather than shared because neither feature may import the
 * other; promote to a shared module if a third table needs it.
 *
 * MUI wraps its own title in an emotion-styled element, so restyling that from
 * outside is a cascade race we lose intermittently. Supplying the element is the
 * pattern the rest of this codebase already uses.
 *
 * Uppercased in CSS, never in the translation source, so the accessible name —
 * which MUI takes from `headerName` — stays the plain phrase.
 */
export const columnHeader = css`
  font-size: ${font_size_sm};
  font-family: ${font_family_weight_medium};
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${text_colorTextSecondary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

/** The terminal column's header, right-aligned over its values. */
export const columnHeaderRight = css`
  ${columnHeader};
  width: 100%;
  text-align: right;
`

/**
 * Shared frame for the loading / empty / error bodies so the panel keeps a
 * stable height as it moves between them and the surrounding Overview sections
 * don't jump on load.
 */
export const statusBody = css`
  ${font_md};
  font-family: ${font_family_weight_regular};
  color: ${text_colorTextSecondary};
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  min-height: ${spacing(24)};
  margin: 0;
`
